import {ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import useAuth from '@/composables/useAuth';
import {useEnvironmentStore} from '@/env/useEnvironmentStore';
import {useConfigStore} from '@/store/config';
import {loadScript} from '@/utils/loadScript';

interface UseChat {
  initChat: () => Promise<boolean>;
}

interface UserVerificationAPI {
  setIdentityToken: (options: {identityTokenType: 'JWT'; identityToken: string}) => void;
  clearSession?: () => void;
}

interface PrechatAPI {
  setHiddenPrechatFields?: (fields: Record<string, string>) => void;
}

interface UtilAPI {
  launchChat?: () => Promise<void>;
}

declare global {
  interface Window {
    embeddedservice_bootstrap?: {
      userVerificationAPI?: UserVerificationAPI;
      prechatAPI?: PrechatAPI;
      utilAPI?: UtilAPI;
      settings?: {orgId?: string};
    };
  }
}

const EMBED_PATH = '/chat/embed.js';
const SESSION_PATH = '/chat/session';

const scriptRequested = ref(false);

/** The shops the verified account is entitled to, as the chat service resolved them. */
const entitledShopIds = ref<number[]>([]);

/**
 * Loads the support chat, through the chat service that gives the conversation its context.
 *
 * The order here is the whole point. Before the widget appears, the service exchanges the viewer's
 * Auth0 token for a Salesforce session and binds the customer's messaging identity - the Contact
 * and the Account - to the *verified* subject. The widget then opens its conversation and Salesforce
 * resolves it to that customer through the binding, so the agent who picks it up sees who they are
 * talking to.
 *
 * The service deliberately does not open the conversation itself. The widget mints its own
 * conversation token from the identity token and accepts neither a token nor a conversation id from
 * us, so a conversation opened server-side is one the customer can never join.
 *
 * The account therefore never travels through the browser and cannot: it is derived server-side from
 * what Salesforce verified. That is the property ADR-004 is about. The shop is different - only the
 * browser knows which of the account's shops the user is looking at - so it travels as a hidden
 * prechat field. It is a *selector*, not a permission: the routing flow accepts it only if it
 * belongs to the bound Account, and the list here is filtered to the entitled shops as well.
 *
 * The identity token itself is the viewer's Auth0 token: Auth0 issues, Salesforce verifies against
 * Auth0's JWKS. Salesforce hands the widget its own conversation token in exchange, so nothing
 * long-lived is kept here. `onEmbeddedMessagingIdentityTokenExpired` gets a fresh one, so a long
 * session does not lose its identity.
 */
const useChat = (): UseChat => {
  const {environment} = useEnvironmentStore();
  const {getToken} = useAuth();
  const {currentShop} = storeToRefs(useConfigStore());

  const setIdentityToken = async (): Promise<void> => {
    const api = window.embeddedservice_bootstrap?.userVerificationAPI;

    if (!api) {
      return;
    }

    try {
      api.setIdentityToken({identityTokenType: 'JWT', identityToken: await getToken()});
    } catch (error) {
      console.warn('Chat identity could not be set', error);
    }
  };

  /**
   * Tells the widget which shop the user is looking at, for as long as it is one of theirs.
   *
   * Sending nothing is the safe outcome: the flow then has no shop to check and the conversation
   * simply carries none, which is what an unrecognised shop should produce anyway.
   */
  const setShopSelector = (): void => {
    const api = window.embeddedservice_bootstrap?.prechatAPI;
    const shopId = currentShop.value?.id;

    if (!api?.setHiddenPrechatFields || !shopId || !entitledShopIds.value.includes(shopId)) {
      return;
    }

    try {
      api.setHiddenPrechatFields({shopIds: String(shopId)});
    } catch (error) {
      console.warn('Chat shop context could not be set', error);
    }
  };

  /**
   * Verifies the viewer through the service, which binds their Contact and Account before the chat
   * opens, and returns the shops that account is entitled to.
   *
   * A failure here stops the chat from loading on purpose. The alternative - load the widget anyway -
   * silently produces a conversation with no customer context, which looks like a working chat and
   * reaches an agent who cannot see who is asking.
   */
  const openVerifiedSession = async (): Promise<boolean> => {
    const response = await fetch(`${environment.chatServiceUrl}${SESSION_PATH}`, {
      headers: {authorization: `Bearer ${await getToken()}`},
      method: 'POST',
    });

    if (!response.ok) {
      console.error(`Chat session was not opened (${response.status}); not loading the chat`);

      return false;
    }

    const session = (await response.json()) as {shopIds?: number[]};

    entitledShopIds.value = session.shopIds ?? [];

    return true;
  };

  const initChat = async (): Promise<boolean> => {
    if (!environment.chatServiceUrl || scriptRequested.value) {
      return false;
    }

    scriptRequested.value = true;

    try {
      if (!(await openVerifiedSession())) {
        return false;
      }

      const onReady = (): void => {
        void setIdentityToken();
        setShopSelector();
      };

      // Both listeners go up before the script loads: the widget can be ready before the next tick.
      window.addEventListener('onEmbeddedMessagingReady', onReady);
      window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', setIdentityToken);

      // Switching shop mid-session has to reach the widget too, or the next conversation carries the
      // shop the user was looking at when the page loaded.
      watch(currentShop, setShopSelector);

      await loadScript(`${environment.chatServiceUrl}${EMBED_PATH}`);

      return true;
    } catch (error) {
      console.warn('Chat was not loaded', error);

      return false;
    }
  };

  return {initChat};
};

export default useChat;
